import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { TenantProvider } from './contexts/TenantContext';
import { LiffProvider } from './contexts/LiffContext';
import { ContextResolutionProvider } from './contexts/ContextResolutionContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Router } from './router';
import './styles/index.css';
import './utils/logger'; // 初始化远程日志上报系统
import { initSentry } from './utils/sentry';

// 初始化 Sentry（必須在所有其他初始化之前）
initSentry();

// 啟動 MSW（僅在開發環境）
if (
  import.meta.env.DEV ||
  import.meta.env.MODE === 'development' ||
  import.meta.env.VITE_ENABLE_LIFF_MOCK === 'true'
) {
  import('./mocks/browser')
    .then(module => {
      module.startMockServiceWorker().catch(error => {
        console.error('[MSW] 啟動失敗:', error);
      });
    })
    .catch(error => {
      console.warn('[MSW] 載入失敗（可能尚未生成 Service Worker）:', error);
    });
}

// 整合 vConsole 除錯工具（開發環境或透過 URL 參數啟用）
if (
  import.meta.env.DEV ||
  new URLSearchParams(window.location.search).has('vconsole')
) {
  // 使用動態導入確保 vConsole 只在需要時載入
  import('vconsole')
    .then(module => {
      try {
        // vConsole 的導出方式可能不同，嘗試多種方式
        const VConsoleClass =
          module.default || (module as any).default?.default || (module as any);
        if (VConsoleClass && typeof VConsoleClass === 'function') {
          new VConsoleClass();
          console.log('[vConsole] ✅ 已啟用手機端除錯工具');
        } else if (
          VConsoleClass &&
          typeof (VConsoleClass as any).VConsole === 'function'
        ) {
          new (VConsoleClass as any).VConsole();
          console.log(
            '[vConsole] ✅ 已啟用手機端除錯工具（使用 VConsole.VConsole）'
          );
        } else {
          console.warn('[vConsole] ⚠️ 無法找到 VConsole 類別', { module });
        }
      } catch (err) {
        console.error('[vConsole] ❌ 初始化失敗:', err);
      }
    })
    .catch(error => {
      console.error('[vConsole] ❌ 載入失敗:', error);
      console.error('[vConsole] 請確認已安裝 vconsole: pnpm add vconsole');
    });
}

// 全域錯誤處理
window.addEventListener('error', event => {
  const errorInfo = {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
      ? {
          name: event.error.name,
          message: event.error.message,
          stack: event.error.stack,
          toString: event.error.toString(),
        }
      : null,
    target: event.target
      ? {
          tagName: (event.target as any).tagName,
          src: (event.target as any).src,
          href: (event.target as any).href,
        }
      : null,
  };
  console.error('[全域錯誤]', errorInfo);
});

window.addEventListener('unhandledrejection', event => {
  const rejectionInfo = {
    reason: event.reason,
    reasonType: typeof event.reason,
    reasonString: String(event.reason),
    reasonStack: event.reason instanceof Error ? event.reason.stack : null,
  };
  console.error('[未處理的 Promise 拒絕]', rejectionInfo);
});

console.log('[main.tsx] 應用程式開始載入');
console.log('[main.tsx] 根元素:', document.getElementById('root'));

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('[main.tsx] 找不到根元素 #root');
    throw new Error('找不到根元素 #root');
  }

  console.log('[main.tsx] 開始渲染 React 應用程式');
  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <TenantProvider>
            <LiffProvider>
              <ContextResolutionProvider>
                <Router />
                {/* 共用 Toaster：所有路由（含 Square /lite/shop/:suffix/*）都會顯示 toast，不需各頁重複掛載 */}
                <Toaster
                  position="top-center"
                  toastOptions={{ duration: 3000 }}
                />
              </ContextResolutionProvider>
            </LiffProvider>
          </TenantProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </StrictMode>
  );
  console.log('[main.tsx] React 應用程式渲染完成');
} catch (error) {
  console.error('[main.tsx] 渲染錯誤:', error);
  if (document.getElementById('root')) {
    document.getElementById('root')!.innerHTML = `
      <div style="padding: 20px; font-family: Arial, sans-serif;">
        <h1 style="color: red;">應用程式載入失敗</h1>
        <p>錯誤訊息: ${error instanceof Error ? error.message : String(error)}</p>
        <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error instanceof Error ? error.stack : String(error)}</pre>
      </div>
    `;
  }
}
